import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Grid, Paper, Skeleton,
} from '@mui/material';
import { Add, LocalDining, Star, CheckCircle, MenuBook, Bookmark, Shuffle } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useRecipes } from '../../contexts/RecipeContext';
import RecipeCard from '../recipes/RecipeCard';

function StatCard({ icon, label, value, color, gradient, onClick }) {
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2.5, flex: 1, position: 'relative', overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        border: `1px solid ${color}25`,
        transition: 'transform 0.15s',
        '&:hover': onClick ? { transform: 'translateY(-2px)' } : {},
        '&::before': {
          content: '""', position: 'absolute', inset: 0,
          background: gradient, opacity: 0.07,
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2, mb: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: gradient,
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 20, color: '#fff' } })}
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{label}</Typography>
      </Box>
    </Paper>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { recipes, recipesLoading } = useRecipes();

  const firstName = user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || 'Chef';
  const starredRecipes = recipes.filter((r) => r.starred);
  const madeRecipes = recipes.filter((r) => r.status === 'made');
  const bookmarkedRecipes = recipes.filter((r) => r.status === 'bookmarked');
  const recentRecipes = recipes.slice(0, 6);

  const handleRandomRecipe = () => {
    if (!recipes.length) return;
    const pick = recipes[Math.floor(Math.random() * recipes.length)];
    navigate(`/recipes/${pick.id}`);
  };

  if (recipesLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={100} sx={{ flex: 1, borderRadius: 3 }} />)}
        </Box>
        <Skeleton variant="text" width={150} height={32} sx={{ mt: 3 }} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={300} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Greeting */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>Hello, {firstName} 👋</Typography>
        <Typography color="text.secondary">What are we cooking today?</Typography>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <StatCard icon={<MenuBook />} label="Total Recipes" value={recipes.length}
          color="#f0873c" gradient="linear-gradient(135deg, #f0873c, #c96820)"
          onClick={() => navigate('/recipes')} />
        <StatCard icon={<CheckCircle />} label="Made It" value={madeRecipes.length}
          color="#6bb07a" gradient="linear-gradient(135deg, #6bb07a, #4a8a58)"
          onClick={() => navigate('/recipes')} />
        <StatCard icon={<Bookmark />} label="Bookmarked" value={bookmarkedRecipes.length}
          color="#5c8fd6" gradient="linear-gradient(135deg, #5c8fd6, #3a6aad)"
          onClick={() => navigate('/recipes')} />
        <StatCard icon={<Star />} label="Starred" value={starredRecipes.length}
          color="#f4c542" gradient="linear-gradient(135deg, #f4c542, #d4a017)"
          onClick={() => navigate('/recipes')} />
      </Box>

      {/* Quick actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/recipes/new')}
          sx={{
            flex: 1, minWidth: 140, py: 1.5,
            background: 'linear-gradient(135deg, #f0873c, #c96820)',
            '&:hover': { background: 'linear-gradient(135deg, #f5a76c, #f0873c)' },
          }}>
          Add Recipe
        </Button>
        <Button variant="outlined" startIcon={<LocalDining />} onClick={() => navigate('/cook')}
          sx={{ flex: 1, minWidth: 140, py: 1.5, borderColor: 'rgba(255,255,255,0.15)' }}>
          What Can I Cook?
        </Button>
        {recipes.length > 0 && (
          <Button variant="outlined" startIcon={<Shuffle />} onClick={handleRandomRecipe}
            sx={{ flex: 1, minWidth: 140, py: 1.5, borderColor: 'rgba(255,255,255,0.15)' }}>
            Surprise Me
          </Button>
        )}
      </Box>

      {recipes.length === 0 ? (
        <Paper sx={{
          p: 6, textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(240,135,60,0.05), rgba(107,176,122,0.05))',
        }}>
          <Typography sx={{ fontSize: '4rem', mb: 2 }}>📖</Typography>
          <Typography variant="h5" sx={{ mb: 1 }}>Your recipe book is empty</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Start by adding your first recipe or importing one from a URL.
          </Typography>
          <Button variant="contained" startIcon={<Add />} size="large" onClick={() => navigate('/recipes/new')}
            sx={{ background: 'linear-gradient(135deg, #f0873c, #c96820)' }}>
            Add Your First Recipe
          </Button>
        </Paper>
      ) : (
        <>
          {/* Starred recipes */}
          {starredRecipes.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">⭐ Starred Recipes</Typography>
                <Button size="small" onClick={() => navigate('/recipes')} sx={{ color: 'primary.main' }}>
                  View all
                </Button>
              </Box>
              <Box sx={{
                display: 'flex', gap: 2, overflowX: 'auto', pb: 1,
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 },
              }}>
                {starredRecipes.map((recipe) => (
                  <Box key={recipe.id} sx={{ minWidth: 260, maxWidth: 280 }}>
                    <RecipeCard recipe={recipe} />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Recently added */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">🕐 Recently Added</Typography>
              <Button size="small" onClick={() => navigate('/recipes')} sx={{ color: 'primary.main' }}>
                View all
              </Button>
            </Box>
            <Grid container spacing={2}>
              {recentRecipes.map((recipe) => (
                <Grid item xs={12} sm={6} lg={4} key={recipe.id}>
                  <RecipeCard recipe={recipe} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
}
